import * as React from 'react'
import { theme } from '../theme'
import { AspectOption, aspectOptions, DimensionVariantImage } from './DimensionOptions'

interface DimensionOptionsPreviewProps {
	selectedOption: AspectOption
	onSelect: (option: AspectOption) => void
	containerWidth: number
	dimensionImages?: Record<string, DimensionVariantImage>
	defaultImageSrc?: string
	registerImageRef?: (dimensionId: string, element: HTMLImageElement | null) => void
	registerContainerRef?: (dimensionId: string, element: HTMLDivElement | null) => void
	onSwitchToAdjustTool?: () => void
}

/**
 * Preview/thumbnail mode for dimension options
 * Shows visual previews of each aspect ratio with the current image
 */
export const DimensionOptionsPreview: React.FC<DimensionOptionsPreviewProps> = ({
	selectedOption,
	onSelect,
	containerWidth,
	dimensionImages = {},
	defaultImageSrc = '',
	registerImageRef,
	registerContainerRef,
	onSwitchToAdjustTool,
}) => {
	// Calculate responsive dimensions
	const thumbnailHeight = 100 // Fixed height in pixels
	const totalGapWidth = 16 * (aspectOptions.length - 1) // 16px gap between thumbnails
	const containerPadding = 16 // 8px padding on each side
	const availableWidth = containerWidth - totalGapWidth - containerPadding

	// Calculate the sum of all aspect ratios to distribute space proportionally
	const totalAspectRatioSum = aspectOptions.reduce((sum, aspect) => sum + aspect.ratio, 0)

	// Calculate the height that would allow all thumbnails to fit within the container
	const responsiveHeight = Math.min(thumbnailHeight, availableWidth / totalAspectRatioSum)

	return (
		<div
			style={{
				display: 'flex',
				gap: '16px',
				justifyContent: 'center',
				marginTop: '8px',
				padding: '8px',
				width: '100%',
				boxSizing: 'border-box',
			}}
		>
			{aspectOptions.map((aspect) => {
				// Calculate width based on aspect ratio and responsive height
				const thumbnailWidth = responsiveHeight * aspect.ratio
				// Get the image for this specific dimension, or fall back to default
				const dimensionImage = dimensionImages[aspect.id]
				const imageSrc = dimensionImage?.url ?? defaultImageSrc
				// Check if the aspect ratio is valid
				const isValid = dimensionImage?.isAspectRatioValid !== false // Default to true if undefined

				return (
					<div
						key={aspect.label}
						onClick={() => {
							// If the image has an invalid aspect ratio and onSwitchToAdjustTool is provided,
							// redirect to adjust tools, otherwise just select the option
							if (!isValid && dimensionImage && onSwitchToAdjustTool) {
								onSelect(aspect) // First select this aspect ratio
								onSwitchToAdjustTool() // Then switch to adjust tools
							} else {
								onSelect(aspect)
							}
						}}
						style={{
							display: 'flex',
							flexDirection: 'column',
							alignItems: 'center',
							cursor: 'pointer',
							opacity: selectedOption.ratio === aspect.ratio ? 1 : 0.7,
							transition: 'opacity 0.2s, transform 0.2s',
							transform: selectedOption.ratio === aspect.ratio ? 'scale(1.05)' : 'scale(1)',
							position: 'relative',
						}}
					>
						<div
							ref={(el) => registerContainerRef?.(aspect.id, el)}
							style={{
								width: `${thumbnailWidth}px`,
								height: `${responsiveHeight}px`,
								border: `2px solid ${
									selectedOption.ratio === aspect.ratio
										? theme.colors.primary
										: theme.colors.border
								}`,
								borderRadius: theme.borderRadius.sm,
								overflow: 'hidden',
								backgroundColor: '#f5f5f5',
								display: 'flex',
								alignItems: 'center',
								justifyContent: 'center',
								position: 'relative', // For positioning the warning indicator
							}}
						>
							{imageSrc && (
								<img
									ref={(el) => registerImageRef?.(aspect.id, el)}
									src={imageSrc}
									alt={`Preview at ${aspect.label}`}
									style={{
										width: '100%',
										height: '100%',
										objectFit: 'contain',
										objectPosition: 'center',
									}}
								/>
							)}

							{/* Warning indicator for invalid aspect ratio */}
							{!isValid && dimensionImage && (
								<div
									style={{
										position: 'absolute',
										top: '4px',
										right: '4px',
										backgroundColor: 'rgba(255, 255, 255, 0.8)',
										borderRadius: '50%',
										width: '18px',
										height: '18px',
										display: 'flex',
										alignItems: 'center',
										justifyContent: 'center',
										color: '#f39c12', // Warning color
										fontSize: '14px',
										zIndex: 2,
										boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
									}}
									title="Image doesn't match the expected aspect ratio"
								>
									⚠️
								</div>
							)}
						</div>
						<span
							style={{
								fontSize: '12px',
								marginTop: '4px',
								color:
									selectedOption.ratio === aspect.ratio
										? theme.colors.primary
										: theme.colors.text.secondary,
								fontWeight: selectedOption.ratio === aspect.ratio ? 600 : 400,
							}}
						>
							{aspect.label}
						</span>
					</div>
				)
			})}
		</div>
	)
}
